// Dashboard · orchestrator only. Tab bodies live in components/dashboard/*.
// This file wires state + fetches + sheet toggles; no big markup lives here.
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import BillingTab from "../components/BillingTab";
import ProposalPanel from "../components/ProposalPanel";
import ReportReviewSheet from "../components/ReportReviewSheet";
import MeetingBriefSheet from "../components/MeetingBriefSheet";
import LinkedInAssetsDialog from "../components/LinkedInAssetsDialog";
import GrowthCampaignCentre from "../components/campaigns/GrowthCampaignCentre";
import NurturePanel from "../components/NurturePanel";
import {
  Users, FileBarChart2, Activity, QrCode,
  Rocket, Target, Sparkles, Handshake, ArrowRight,
  CheckCircle2, Circle, FileText, PlusCircle, Send, Zap, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { StatCard } from "../components/dashboard/primitives";
import LeadsTable from "../components/dashboard/LeadsTable";
import TopOpportunitiesCard from "../components/dashboard/TopOpportunitiesCard";
import ProfileTab from "../components/dashboard/ProfileTab";
import BrandingTab from "../components/dashboard/BrandingTab";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState("home");
  const [stats, setStats] = useState(null);
  const [qr, setQr] = useState(null);
  const [catalog, setCatalog] = useState({ countries: [], industries: [], functions: [] });
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [proposalLead, setProposalLead] = useState(null);
  const [reviewLead, setReviewLead] = useState(null);
  const [briefLead, setBriefLead] = useState(null);
  const [linkedinLink, setLinkedinLink] = useState(null);
  const [nurtureLead, setNurtureLead] = useState(null);
  const [nurtureStatus, setNurtureStatus] = useState({});
  const [benchmarks, setBenchmarks] = useState({});
  const [sampleViewed, setSampleViewed] = useState(
    typeof window !== "undefined" && window.localStorage?.getItem("bp_viewed_sample_report") === "1"
  );

  const markSampleViewed = () => {
    try { window.localStorage.setItem("bp_viewed_sample_report", "1"); } catch (_e) { /* ignore write failures */ }
    setSampleViewed(true);
    window.open("/sample-report", "_blank", "noopener");
  };

  const loadQr = () => api.get("/consultants/me/qr").then((r) => setQr(r.data));

  // Load nurture status (draft/scheduled/cancelled/none) for each lead once stats arrive
  useEffect(() => {
    if (!stats?.recent_leads) return;
    const unlocked = stats.recent_leads.filter((l) => l.unlocked);
    if (!unlocked.length) return;
    Promise.all(
      unlocked.map((l) =>
        api.get(`/leads/${l.id}/nurture`).then((r) => [l.id, r.data?.sequence?.status || "none"]).catch(() => [l.id, "none"])
      )
    ).then((pairs) => {
      const map = {};
      for (const [id, s] of pairs) map[id] = s;
      setNurtureStatus(map);
    });
  }, [stats]);

  // Batch-fetch benchmark percentiles for every visible lead in ONE call.
  useEffect(() => {
    if (!stats?.recent_leads?.length) return;
    const ids = stats.recent_leads.map((l) => l.id);
    api
      .post("/consultants/me/leads/benchmark-batch", { lead_ids: ids })
      .then((r) => setBenchmarks(r.data?.results || {}))
      .catch(() => setBenchmarks({}));
  }, [stats]);

  useEffect(() => {
    api.get("/consultants/me/dashboard").then((r) => setStats(r.data));
    loadQr();
    api.get("/catalog").then((r) => setCatalog(r.data));
    setProfile({
      name: user.name || "", company: user.company || "", biography: user.biography || "",
      slug: user.slug || "",
      expertise: user.expertise || [], countries: user.countries || [], industries: user.industries || [],
      functions: user.functions || [], whatsapp: user.whatsapp || "", booking_link: user.booking_link || "",
      profile_photo: user.profile_photo || "",
      primary_language: user.primary_language || "en",
      additional_languages: user.additional_languages || [],
    });
  }, [user]);

  useEffect(() => {
    setBrand({
      subdomain: user.subdomain || "",
      brand_logo_url: user.brand_logo_url || "",
      brand_primary: user.brand_primary || "",
      brand_accent: user.brand_accent || "",
      brand_headline: user.brand_headline || "",
      brand_subheadline: user.brand_subheadline || "",
    });
  }, [user]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/consultants/me", profile);
      setUser({ ...data, role: "consultant" });
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.put("/consultants/me/brand", brand);
      setUser({ ...data, role: "consultant" });
      await loadQr();
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save branding");
    } finally {
      setSavingBrand(false);
    }
  };

  if (!stats || !qr || !profile || !brand) return <div className="p-10 text-muted-foreground">Loading dashboard…</div>;

  // -- Growth Engine status (derived from existing state, no new APIs) --
  const engineStages = [
    { key: "attract",  label: "Attract",  done: !!user.slug },
    { key: "diagnose", label: "Diagnose", done: (user.functions?.length || 0) > 0 && (user.countries?.length || 0) > 0 },
    { key: "convert",  label: "Convert",  done: (stats.total_leads || 0) > 0 },
    { key: "nurture",  label: "Nurture",  done: Object.values(nurtureStatus).some((s) => s === "scheduled" || s === "draft") },
    { key: "grow",     label: "Grow",     done: (stats.assessments_completed || 0) > 0 },
  ];
  const engineDone = engineStages.filter((s) => s.done).length;

  // -- Today's Business Snapshot (reuses existing metrics) --
  const snap = {
    started:        stats.assessments_started || 0,
    leads:          stats.total_leads || 0,
    reports:        stats.assessments_completed || 0,
    meetings:       (stats.recent_leads || []).filter((l) => l.unlocked).length,
    proposals:      (stats.recent_leads || []).filter((l) => l.proposal_status && l.proposal_status !== "none").length,
    nurtureRunning: Object.values(nurtureStatus).filter((s) => s === "scheduled" || s === "draft").length,
  };

  // -- 5 stage cards on the Growth Engine home tab --
  const stageCards = [
    { key: "attract",  stage: "01 · Attract",  title: "Bring executives in",     icon: Target,     target: "share",
      modules: ["Campaigns", "Landing Pages", "QR Codes", "Share Links", "LinkedIn Assets"] },
    { key: "diagnose", stage: "02 · Diagnose", title: "Assess their business",   icon: Activity,   target: "leads",
      modules: ["Assessments", "Business Growth Reports", "Benchmarks", "Grounding"] },
    { key: "convert",  stage: "03 · Convert",  title: "Book & propose",          icon: Handshake,  target: "leads",
      modules: ["Proposal Builder", "Discovery Meetings", "WhatsApp", "Email Templates"] },
    { key: "nurture",  stage: "04 · Nurture",  title: "Stay top-of-mind",        icon: Sparkles,   target: "leads",
      modules: ["Email Sequences", "LinkedIn Content", "Follow-ups", "Marketing Assets"] },
    { key: "grow",     stage: "05 · Grow",     title: "Compound your practice",  icon: TrendingUp, target: "billing",
      modules: ["Analytics", "Business Insights", "Pipeline", "Performance", "Subscription"] },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="consultant-dashboard">
      {/* ============================ Hero ============================ */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline"><span className="accent-line" /> The Consulting Growth Engine™</div>
          <h1 className="font-display text-4xl mt-3" data-testid="dashboard-hero-title">
            Welcome to your <span className="italic text-accent">Consulting Growth Engine™</span>
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Everything you need to attract, diagnose, convert, nurture and grow your consulting business from one platform.
          </p>
        </div>
      </div>

      {/* ==================== Growth Engine status ==================== */}
      <Card className="card-elevated p-5 mt-8" data-testid="growth-engine-status">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="overline flex items-center gap-2">
            <Rocket className="w-3.5 h-3.5 text-accent" /> Growth Engine status
          </div>
          <div className="text-xs text-muted-foreground font-sans-ui" data-testid="engine-progress">
            {engineDone} of 5 stages configured
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {engineStages.map((s) => (
            <div
              key={s.key}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 ${
                s.done ? "border-accent/40 bg-accent/10" : "border-border/60 bg-secondary/30"
              }`}
              data-testid={`engine-stage-${s.key}`}
            >
              {s.done ? (
                <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-muted-foreground/50 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-sans-ui font-semibold text-sm">{s.label}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {s.done ? "Configured" : "Not started"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ==================== Today's Business Snapshot ==================== */}
      <div className="mt-10">
        <div className="overline">Today&apos;s business snapshot</div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-4">
          <StatCard icon={Activity}      label="Assessments Started"      value={snap.started}        testid="snap-started" />
          <StatCard icon={Users}         label="Qualified Leads"          value={snap.leads}          testid="snap-leads" />
          <StatCard icon={FileBarChart2} label="Reports Generated"        value={snap.reports}        testid="snap-reports" />
          <StatCard icon={Handshake}     label="Discovery Meetings"       value={snap.meetings}       testid="snap-meetings" />
          <StatCard icon={FileText}      label="Proposals Created"        value={snap.proposals}      testid="snap-proposals" />
          <StatCard icon={Sparkles}      label="Nurture Campaigns Running" value={snap.nurtureRunning} testid="snap-nurture" />
        </div>
      </div>

      {/* ================================ Tabs ================================ */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-12">
        <TabsList className="bg-card/60">
          <TabsTrigger value="home" data-testid="tab-home">Growth Engine</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="share" data-testid="tab-share">Campaigns</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
        </TabsList>

        {/* -- Home tab · 5 stage cards + Quick actions -- */}
        <TabsContent value="home" className="mt-6 space-y-8">
          {/* -- Activation checklist: Complete Your First Consulting Journey -- */}
          {(() => {
            const checklist = [
              {
                key: "sample",
                title: "View Sample Report",
                why: "See exactly what your business leaders will receive so you can sell the outcome with confidence.",
                done: sampleViewed,
                action: markSampleViewed,
                cta: "View sample",
              },
              {
                key: "campaign",
                title: "Create Your First Campaign",
                why: "Every consulting client starts with a branded diagnostic — spin one up in under three minutes.",
                done: !!user.slug,
                action: () => setActiveTab("share"),
                cta: "Open Campaigns",
              },
              {
                key: "share",
                title: "Share Your Assessment Link",
                why: "The moment a leader completes it, they land in your pipeline as a qualified lead.",
                done: (stats.assessments_started || 0) > 0,
                action: () => setActiveTab("share"),
                cta: "Get share link",
              },
              {
                key: "lead",
                title: "Capture Your First Lead",
                why: "Your first qualified lead is proof the engine works — and the moment revenue becomes real.",
                done: (stats.total_leads || 0) > 0,
                action: () => setActiveTab("leads"),
                cta: "Open Leads",
              },
              {
                key: "proposal",
                title: "Generate Your First Proposal",
                why: "Turn any completed report into an engagement-ready proposal the same day the lead lands.",
                done: (stats.recent_leads || []).some((l) => l.proposal_status && l.proposal_status !== "none"),
                action: () => setActiveTab("leads"),
                cta: "Draft proposal",
              },
              {
                key: "nurture",
                title: "Launch Your First Nurture Campaign",
                why: "Silence loses deals — automated follow-up keeps your practice top-of-mind between meetings.",
                done: Object.values(nurtureStatus).some((s) => s === "scheduled" || s === "draft"),
                action: () => setActiveTab("leads"),
                cta: "Start nurture",
              },
            ];
            const doneCount = checklist.filter((c) => c.done).length;
            const total = checklist.length;
            const pct = Math.round((doneCount / total) * 100);
            const allDone = doneCount === total;

            return (
              <Card className="card-elevated p-5 sm:p-6" data-testid="activation-checklist">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="overline flex items-center gap-2">
                    <Rocket className="w-3.5 h-3.5 text-accent" /> Complete Your First Consulting Journey
                  </div>
                  <div
                    className="text-xs font-sans-ui uppercase tracking-widest text-muted-foreground"
                    data-testid="activation-progress-count"
                  >
                    {doneCount} of {total} complete
                  </div>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-border/60 overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all"
                    style={{ width: `${pct}%` }}
                    data-testid="activation-progress-bar"
                  />
                </div>

                {allDone && (
                  <div
                    className="mt-5 rounded-lg border border-accent/40 bg-accent/10 p-4 flex items-start gap-3"
                    data-testid="activation-complete-banner"
                  >
                    <Sparkles className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <div>
                      <div className="font-sans-ui font-semibold text-foreground">
                        🎉 Your Consulting Growth Engine™ is fully operational.
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        You&apos;re ready to attract real consulting clients.
                      </div>
                    </div>
                  </div>
                )}

                <ul className="mt-5 divide-y divide-border/50" data-testid="activation-checklist-items">
                  {checklist.map((item, i) => (
                    <li
                      key={item.key}
                      className="py-3 flex items-start gap-4"
                      data-testid={`activation-item-${item.key}`}
                    >
                      <div className="shrink-0 mt-0.5">
                        {item.done ? (
                          <CheckCircle2 className="w-5 h-5 text-accent" />
                        ) : (
                          <Circle className="w-5 h-5 text-muted-foreground/50" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-sans-ui">
                            0{i + 1}
                          </span>
                          <span
                            className={`font-sans-ui font-semibold text-sm ${
                              item.done ? "text-muted-foreground line-through" : "text-foreground"
                            }`}
                          >
                            {item.title}
                          </span>
                          {item.done && (
                            <span className="text-[10px] uppercase tracking-widest text-accent font-sans-ui">
                              Complete
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground leading-relaxed">
                          {item.why}
                        </div>
                      </div>
                      <div className="shrink-0">
                        <Button
                          variant={item.done ? "ghost" : "outline"}
                          size="sm"
                          onClick={item.action}
                          data-testid={`activation-cta-${item.key}`}
                        >
                          {item.done ? "Revisit" : item.cta}
                          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })()}

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stage-cards">
            {stageCards.map((c) => (
              <Card
                key={c.key}
                className="card-elevated p-5 flex flex-col"
                data-testid={`stage-card-${c.key}`}
              >
                <c.icon className="w-5 h-5 text-accent" />
                <div className="mt-3 text-[10px] uppercase tracking-widest text-accent/90 font-sans-ui">
                  {c.stage}
                </div>
                <div className="font-sans-ui font-semibold text-base mt-1">{c.title}</div>
                <ul className="mt-3 space-y-1 flex-1">
                  {c.modules.map((m) => (
                    <li key={m} className="text-xs text-muted-foreground">· {m}</li>
                  ))}
                </ul>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 w-full"
                  onClick={() => setActiveTab(c.target)}
                  data-testid={`stage-open-${c.key}`}
                >
                  Open <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Card>
            ))}
          </div>

          <Card className="card-elevated p-5" data-testid="quick-actions">
            <div className="overline flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-accent" /> Quick actions
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
              <Button variant="outline" size="sm" className="justify-start" onClick={() => setActiveTab("share")} data-testid="qa-create-campaign">
                <PlusCircle className="w-4 h-4 mr-2" /> Create campaign
              </Button>
              <Button variant="outline" size="sm" className="justify-start" onClick={() => window.open("/sample-report", "_blank", "noopener")} data-testid="qa-sample-report">
                <FileBarChart2 className="w-4 h-4 mr-2" /> View sample report
              </Button>
              <Button variant="outline" size="sm" className="justify-start" onClick={() => setActiveTab("leads")} data-testid="qa-proposal">
                <FileText className="w-4 h-4 mr-2" /> Generate proposal
              </Button>
              <Button variant="outline" size="sm" className="justify-start" onClick={() => setActiveTab("share")} data-testid="qa-linkedin">
                <Send className="w-4 h-4 mr-2" /> LinkedIn content
              </Button>
              <Button variant="outline" size="sm" className="justify-start" onClick={() => setActiveTab("share")} data-testid="qa-launch">
                <Rocket className="w-4 h-4 mr-2" /> Launch assessment
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="mt-6 grid lg:grid-cols-3 gap-6">
          <LeadsTable
            leads={stats.recent_leads}
            nurtureStatus={nurtureStatus}
            benchmarks={benchmarks}
            onOpenNurture={setNurtureLead}
            onOpenReview={setReviewLead}
            onOpenBrief={setBriefLead}
            onOpenProposal={setProposalLead}
          />
          <TopOpportunitiesCard gaps={stats.top_gap_areas} />
        </TabsContent>

        <TabsContent value="share" className="mt-6">
          <GrowthCampaignCentre />
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <ProfileTab
            profile={profile}
            setProfile={setProfile}
            catalog={catalog}
            saving={saving}
            onSave={saveProfile}
          />
        </TabsContent>

        <TabsContent value="branding" className="mt-6">
          <BrandingTab
            brand={brand}
            setBrand={setBrand}
            saving={savingBrand}
            onSave={saveBrand}
          />
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          <BillingTab currentUser={user} onPlanChanged={() => {
            api.get("/auth/me").then((r) => setUser({ ...r.data, role: "consultant" }));
          }} />
        </TabsContent>
      </Tabs>

      <ProposalPanel open={!!proposalLead} lead={proposalLead} onOpenChange={(o) => { if (!o) setProposalLead(null); }} />
      <LinkedInAssetsDialog open={!!linkedinLink} link={linkedinLink} onOpenChange={(o) => { if (!o) setLinkedinLink(null); }} />
      <NurturePanel
        open={!!nurtureLead}
        lead={nurtureLead}
        onOpenChange={(o) => { if (!o) setNurtureLead(null); }}
        onSequenceChange={(lid, seq) => setNurtureStatus((prev) => ({ ...prev, [lid]: seq?.status || "none" }))}
      />
      <ReportReviewSheet
        open={!!reviewLead}
        lead={reviewLead}
        onOpenChange={(o) => { if (!o) setReviewLead(null); }}
        onApproved={(lid) => {
          setStats((s) => s ? {
            ...s,
            recent_leads: (s.recent_leads || []).map((l) => l.id === lid ? { ...l, report_status: "approved" } : l),
          } : s);
        }}
      />
      <MeetingBriefSheet open={!!briefLead} lead={briefLead} onOpenChange={(o) => { if (!o) setBriefLead(null); }} />
    </div>
  );
}
